import { Box } from '@mui/material';
import { validatePermissions } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { User } from 'services/redux/authSlice';

import CommitteeChangeDialog from './CommitteeChangeDialog';
import CommitteeInfoCard from './CommitteeInfoCard';
import CommitteeTabs from './CommitteeTabs';
import {
  Approval,
  ApprovalRole,
  Committee as CommitteeType,
  CommitteeService,
} from './types';

interface CommitteeProps {
  committeeService: CommitteeService;
}

const Committee = ({ committeeService }: CommitteeProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [approvalsRequired, setApprovalsRequired] = useState<ApprovalRole[]>(
    [],
  );
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [comitee, setComitee] = useState<CommitteeType | null>(null);
  const [table, setTable] = useState<string>(
    committeeService.isActive ? 'approvals' : 'comitee',
  );
  const [openCommitteeChange, setOpenCommitteeChange] = useState(false);
  const [userRoles, setUserRoles] = useState<User['roles']>([]);
  const [selectedUserRoleId, setSelectedUserRoleId] = useState<number | null>(
    null,
  );

  const canEdit = committeeService.canEdit;

  const canChangeCommittee =
    canEdit && validatePermissions(user, 'comitee_change');
  const canDeleteApproval =
    canEdit && validatePermissions(user, 'comitee_delete_approval');

  const setUpApprovals = async () => {
    setLoading(true);
    try {
      const responseApprovals = await committeeService.getMissingApprovals(
        dispatch,
        committeeService.requestId,
      );
      setApprovalsRequired(responseApprovals.rolesToApprove);
      setApprovals(responseApprovals.approvals);
      setComitee(responseApprovals.comitee);
      const userRolesList = Array.isArray(user?.roles) ? user?.roles : [];
      const requiredRoleIds = new Set(
        responseApprovals.rolesToApprove
          .filter((r: ApprovalRole) => !r.approved)
          .map((r: ApprovalRole) => r.id),
      );
      const approvedRoleIds = new Set(
        responseApprovals.approvals
          .filter((a: Approval) => a.userId === user?.id)
          .map((a: Approval) => a.roleId),
      );
      const userRolesWithoutApproval = userRolesList?.filter(
        (userRole) =>
          requiredRoleIds.has(userRole.id) && !approvedRoleIds.has(userRole.id),
      );
      const canApprove =
        Boolean(userRolesWithoutApproval?.length) &&
        responseApprovals.rolesToApprove.some(
          (entry: ApprovalRole) => entry.userCanApprove,
        );
      setCanApprove(canApprove);

      setUserRoles(userRolesWithoutApproval ?? []);
      if (userRolesWithoutApproval?.length === 1) {
        setSelectedUserRoleId(userRolesWithoutApproval[0].id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reason: string, roleId: number) => {
    try {
      setLoading(true);
      await committeeService.approve(dispatch, committeeService.requestId, {
        reason,
        roleId,
      });
      showAlert(dispatch, 'success', 'Aprobación realizada exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al realizar la aprobación');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleReject = async (reason: string, roleId: number) => {
    try {
      setLoading(true);
      await committeeService.reject(dispatch, committeeService.requestId, {
        reason,
        roleId,
      });
      showAlert(dispatch, 'success', 'Rechazo realizado exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al realizar el rechazo');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleCommitteeChange = async (comiteeId: string) => {
    try {
      setLoading(true);
      await committeeService.updateCommittee(
        dispatch,
        committeeService.requestId,
        {
          comiteeId,
        },
      );
      showAlert(dispatch, 'success', 'Comité actualizado exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al actualizar el comité');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleDeleteApproval = async (approvalId: number) => {
    try {
      setLoading(true);
      await committeeService.deleteApproval(
        dispatch,
        committeeService.requestId,
        approvalId,
      );
      showAlert(dispatch, 'success', 'Aprobación eliminada exitosamente');
      await setUpApprovals();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al eliminar la aprobación');
      await setUpApprovals();
    }
    setLoading(false);
  };

  const handleEditCommitteeClick = () => {
    setOpenCommitteeChange(true);
  };

  useEffect(() => {
    void setUpApprovals();
  }, []);

  return (
    <Box sx={{ my: 2 }}>
      <CommitteeInfoCard
        comitee={comitee}
        onEditClick={canChangeCommittee ? handleEditCommitteeClick : undefined}
        canEdit={canChangeCommittee}
        loading={loading}
      />

      {comitee && (
        <CommitteeTabs
          table={table}
          setTable={setTable}
          approvalsRequired={approvalsRequired}
          approvals={approvals}
          handleApprove={handleApprove}
          handleReject={handleReject}
          canApprove={canApprove}
          canEdit={canEdit}
          onDeleteApproval={handleDeleteApproval}
          canDeleteApproval={canDeleteApproval}
          userRoles={userRoles}
          selectedUserRoleId={selectedUserRoleId}
          setSelectedUserRoleId={setSelectedUserRoleId}
          isActive={committeeService.isActive}
          loading={loading}
        />
      )}

      {openCommitteeChange && (
        <CommitteeChangeDialog
          open={openCommitteeChange}
          setOpen={setOpenCommitteeChange}
          currentComitee={comitee}
          onSave={handleCommitteeChange}
          onCancel={() => setOpenCommitteeChange(false)}
        />
      )}
    </Box>
  );
};

export default Committee;
